import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import './CreateOrganizationMember.css';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import loadingGif from '../../assets/loading.gif';
import { IconButton } from '@mui/material';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from '@mui/material';

const CreateOrganizationMember = () => {
  const [memberData, setMemberData] = useState(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPassword, setMemberPassword] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberNumber, setMemberNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/users/admin/member-info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization info');
        }

        const data = await response.json();
        setMemberData(data);
      } catch (error) {
        console.error('Error fetching admin status:', error);
        navigate('/unauthorized');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminStatus();
  }, [navigate]);

  const calculateUsagePercentage = () => {
    if (memberData) {
      return Math.floor((memberData.member_count / memberData.max_member) * 100);
    }
    return 0;
  };

  const isCreateButtonEnabled = memberData && memberData.member_count < memberData.max_member;

  const handleCreateMember = async () => {
    if (!memberEmail || !memberPassword || !memberFirstName || !memberLastName || !memberNumber) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill out all fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } else {
      try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');
  
        const response = await fetch(`${API_BASE_URL}/org-members/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
          body: JSON.stringify({
            memberEmail: memberEmail,
            memberPassword: memberPassword,
            memberFirstName,
            memberLastName,
            memberNumber,
          }),
        });
  
        if (response.status === 201) {
          const data = await response.json();
  
          Swal.fire({
            title: 'Success!',
            text: 'Member created successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/organization/members');
          });
        } else if (response.status === 409) {
          Swal.fire({
            title: 'Conflict',
            text: 'The member already exists in the organization.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
        } else {
          throw new Error('Failed to create member');
        }
  
      } catch (error) {
        console.error('Error creating member:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to create member.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const usagePercentage = calculateUsagePercentage();

  if (loading) {
    return (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
    }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
    </div>
    );
  }
  

  return (
    <Box sx={{ width: '100%', margin: 'auto', mt: 0 }}>
          <Card sx={{ width: '100%', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <IconButton onClick={() => navigate('/organization/members')} sx={{ mr: 2 }}>
                <KeyboardReturnRoundedIcon />
              </IconButton>
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                Create Organization Member
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              {memberData && (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Member Usage: {memberData.member_count} / {memberData.max_member} members
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <LinearProgress variant="determinate" value={usagePercentage} sx={{ height: 10, borderRadius: 5 }} />
                  </Box>
                </>
              )}
              <Divider />
              <Grid item xs={12} md={12} sx={{ mt: 3 }}>
                <div className="input-container">
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Memeber Email
                  </Typography>
                  <input
                    type="text"
                    value={memberEmail}
                    onChange={(e) => setMemberEmail(e.target.value)}
                    placeholder="Email"
                    maxLength={255}
                  />

                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Member Password
                  </Typography>
                  <div className="password-container">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={memberPassword}
                      onChange={(e) => setMemberPassword(e.target.value)}
                      placeholder="Password"
                      maxLength={255}
                    />
                    <button
                      type="button"
                      className="show-password-button"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Member First Name
                  </Typography>
                  <input
                    type="text"
                    value={memberFirstName}
                    onChange={(e) => setMemberFirstName(e.target.value)}
                    placeholder="First Name"
                    maxLength={255}
                  />

                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Member Last Name
                  </Typography>
                  <input
                    type="text"
                    value={memberLastName}
                    onChange={(e) => setMemberLastName(e.target.value)}
                    placeholder="Last Name"
                    maxLength={255}
                  />

                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Member Phone Number
                  </Typography>
                  <input
                    type="text"
                    value={memberNumber}
                    onChange={(e) => setMemberNumber(e.target.value)}
                    placeholder="Phone Number"
                    maxLength={20}
                  />
                </div>
              </Grid>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateMember}
                disabled={!isCreateButtonEnabled}
                sx={{ mt: 3 }}
              >
                Create Member
              </Button>
              {!isCreateButtonEnabled && (
                <Typography color="error" sx={{ mt: 2 }}>
                  * You have reached the maximum limit for roles allowed under your plan. Please upgrade your plan or delete an existing role before creating a new one.
                </Typography>
              )}
            </Box>
          </Card>
        </Box>
  );
};

export default CreateOrganizationMember;
